import { JSX, useMemo } from 'react';
import { IToolbarItemProps, Item } from 'devextreme-react/data-grid';
import { DataGridPredefinedToolbarItem } from 'devextreme/ui/data_grid_types';
import { ExportButton, GridResetButton } from '../components/ToolbarDefaults';
import { GridCustomToolbarComponent, NewColumn } from '../datagrid-types';

interface UseToolbarItemsProps {
  resetState: () => void;
  disableLayoutReset: boolean;
  customToolbarComponents?: GridCustomToolbarComponent[];
  storageKey?: string;
  filter?: string;
  allowExport?: boolean;
  columns?: NewColumn[];
}

/**
 * Generates the toolbar items for the data grid.
 */

const useToolbarItems = ({
  resetState,
  disableLayoutReset,
  customToolbarComponents = [],
  storageKey = '',
  filter = '',
  allowExport = false,
  columns
}: UseToolbarItemsProps): JSX.Element[] => {
  const combinedArray: Array<IToolbarItemProps | DataGridPredefinedToolbarItem> = useMemo(
    () => [
      'groupPanel',
      { name: 'searchPanel', location: 'before' },
      ...customToolbarComponents,
      {
        id: 'resetButton',
        name: 'resetButton',
        location: 'after',
        toolbar: 'top',
        visible: !disableLayoutReset,
        render: () => <GridResetButton resetState={resetState} />
      },
      'columnChooserButton',
      {
        id: 'exportButton',
        name: 'exportButton',
        location: 'after',
        toolbar: 'top',
        visible: allowExport,
        render: () => <ExportButton storageKey={storageKey} filter={filter} />
      }
    ],

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allowExport, columns, customToolbarComponents, disableLayoutReset, filter, storageKey]
  );

  const generateToolbarItems = useMemo(() => {
    return combinedArray.map(item => {
      if (typeof item === 'string') return <Item name={item} key={item} />;

      return <Item {...item} key={(item as GridCustomToolbarComponent).name} />;
    });
  }, [combinedArray]);

  return generateToolbarItems;
};

export default useToolbarItems;
